import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  createStyles,
  makeStyles,
  Theme,
  useMediaQuery,
} from "@material-ui/core"
import React from "react"
import ChannelMenu from "./ChannelMenu"
import {
  loadMinifiedPlugins,
  preferenceCenterId,
  smsCountry,
  smsSenderId,
  smsEnabled,
  emailEnabled,
} from "./globals"
import {
  Menu as MenuIcon,
  Settings as SettingsIcon,
  Tune as TuneIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from "@material-ui/icons"
import { useSubscriptionModal } from "./hooks/use-sdk-plugin"
import { useAppDispatch } from "./store"
import { useHistory } from "react-router-dom"

export type Props = {
  setMenuOpen: (open: boolean) => void
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    menuButton: {
      marginRight: theme.spacing(2),
    },
    title: {
      flexGrow: 1,
    },
  })
)

const EmailModalButton: React.FC = () => {
  const dispatch = useAppDispatch()
  const { result: emailModal } = useSubscriptionModal(
    {
      platform: "email",
      doubleOptIn: true,
      i18n: {
        en: {
          heading: "Airship emails, you will love them!",
          terms: `By signing up for emails, you are agreeing to the <a href="https://www.airship.com/legal/overview/" target="_blank">Airship Terms of Use</a>.`,
          invalidEmail: "The email you entered is invalid, please try again.",
          invalidMsisdn:
            "The phone number you entered is invalid, please try again.",
          submitButton: "I love emails",
          placeholderEmail: "me@domain.example",
          placeholderMsisdn: "+1 (503) 555-1212",
          genericError: "Something went wrong, please try again.",
          successHeading: "Great job",
          successMessage: "It's a success",
        },
      },
      meta: { loadMinified: loadMinifiedPlugins },
    },
    [dispatch]
  )

  return (
    <IconButton
      data-qa-selector="email-signup-button"
      color="inherit"
      aria-label="Sign up for Emails"
      onClick={() => emailModal?.open()}
    >
      <EmailIcon />
    </IconButton>
  )
}

const SmsModalButton: React.FC = () => {
  const dispatch = useAppDispatch()
  const { result: smsModal } = useSubscriptionModal(
    {
      platform: "sms",
      senderId: smsSenderId,
      country: smsCountry,
      i18n: {
        en: {
          heading: "Airship text messages, you will love them!",
          terms:
            "By entering your mobile number and signing up, you are agreeing " +
            "to receive a confirmation text message and periodic promotional " +
            "messages from an automated telephone dialing system, and " +
            "confirming that you have read and accept our " +
            '<a href="#">Terms & Conditions</a> and ' +
            '<a href="#">Privacy Policy</a>. Consent to future text messages ' +
            "is not a condition of purchase. Msg & Data rates may apply. Text " +
            "STOP if you no longer wish to receive text messages.",
          submitButton: "Sign me up",
          invalidEmail: "The email you entered is invalid, please try again.",
          invalidMsisdn:
            "The phone number you entered is invalid, please try again.",
          placeholderEmail: "me@domain.example",
          placeholderMsisdn: "+1 (503) 555-1212",
          genericError: "Something went wrong, please try again.",
          successHeading: "Great job",
          successMessage: "It's a success",
        },
      },
      meta: { loadMinified: loadMinifiedPlugins },
    },
    [dispatch]
  )

  return (
    <IconButton
      data-qa-selector="sms-signup-button"
      color="inherit"
      aria-label="Sign up for Text Messages"
      onClick={() => smsModal?.open()}
    >
      <PhoneIcon />
    </IconButton>
  )
}

const Navbar: React.FC<Props> = ({ setMenuOpen }) => {
  const classes = useStyles()
  const history = useHistory()
  const isLargeScreen = useMediaQuery("(min-width:600px)")

  const navigate = (url: string): void => {
    history.push(url)
    setMenuOpen(false)
  }

  return (
    <AppBar position="static">
      <Toolbar>
        <IconButton
          data-qa-selector="menu-botton"
          edge="start"
          className={classes.menuButton}
          color="inherit"
          aria-label="menu"
          onClick={() => setMenuOpen(true)}
        >
          <MenuIcon />
        </IconButton>
        <Typography variant="h6" className={classes.title}>
          {isLargeScreen ? "Web Push Demo" : "Demo"}
        </Typography>
        <ChannelMenu />
        {emailEnabled ? <EmailModalButton /> : <></>}
        {smsEnabled ? <SmsModalButton /> : <></>}
        <IconButton
          data-qa-selector="preferences-button"
          color="inherit"
          aria-label="Notification Preferences"
          onClick={() => navigate(`preferences/${preferenceCenterId}`)}
        >
          <TuneIcon />
        </IconButton>
        <IconButton
          data-qa-selector="settings-button"
          color="inherit"
          aria-label="settings"
          onClick={() => navigate("/settings")}
        >
          <SettingsIcon />
        </IconButton>
      </Toolbar>
    </AppBar>
  )
}

export default Navbar
